import React, { useEffect, useState } from "react";
import { api, fmtDate, fmtINR, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Printer, Zap, TrendingUp, Landmark, Pencil, Trash2, FileCheck, ShieldCheck, PlusCircle, Trash } from "lucide-react";
import { SearchBox, matchesQuery } from "@/components/crm/RecordActions";
import FilterBar, { inDateRange } from "@/components/crm/Filters";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import WhatsAppShareButton from "@/components/crm/WhatsAppShareButton";

const empty = {
  customer_id: "", quotation_no: "", system_size_kw: 10, tilt_angle: null,
  panel_brand: "WAAREE", panel_wattage: "585-615 Wp", panel_type: "Bifacial",
  inverter_brand: "SUNGROW", inverter_capacity: "15 kW", structure_type: "HDGI",
  dc_wire_size: "4 sq.mm", dc_wire_brand: "Polycab",
  earthing_wire_size: "10 sq.mm", earthing_wire_brand: "Polycab",
  armoured_cable_size: "2C x 4 sq.mm", armoured_cable_brand: "Polycab",
  earthing_rod: "2m GI/Copper Bonded (x3)", lightning_arrester: "1 No. with down conductor",
  acdb_brand: "True Power", dcdb_brand: "True Power",
  system_cost: 520000, gst_percent: 8.9, subsidy: 78000, discount: 0,
  electricity_rate: 8, loan_amount: 0, loan_tenor_months: 60, loan_interest: 8, notes: "",
  warranty_text: "Panel: 25 years performance · Inverter: 10 years · Cables/Structure: 30 years · Installation workmanship: 2 years.",
  additional_items: [],
};

function calcEmi(p, annualRate, months) {
  if (!p || !months) return { emi: 0, total: 0, interest: 0 };
  const r = annualRate / 1200;
  const emi = r > 0 ? (p * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1) : p / months;
  return { emi: Math.round(emi), total: Math.round(emi * months), interest: Math.round(emi * months - p) };
}

export default function Quotations() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const load = async () => {
    setItems((await api.get("/quotations")).data);
    setCustomers((await api.get("/customers")).data);
  };
  useEffect(() => { load(); }, []);

  const num = (v) => parseFloat(v) || 0;
  const gst = num(form.system_cost) * num(form.gst_percent) / 100;
  const finalCost = num(form.system_cost) + gst - num(form.subsidy) - num(form.discount);
  const unitsDay = num(form.system_size_kw) * 4.31;
  const yearlyUnits = unitsDay * 365;
  const yearlySavings = yearlyUnits * num(form.electricity_rate);
  const payback = yearlySavings > 0 ? finalCost / yearlySavings : 0;
  const loan = calcEmi(num(form.loan_amount), num(form.loan_interest), num(form.loan_tenor_months));

  const save = async () => {
    try {
      if (editId) await api.patch(`/quotations/${editId}`, form);
      else await api.post("/quotations", form);
      toast.success(editId ? "Quotation updated" : "Quotation created");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const edit = (q) => { setForm({ ...empty, ...q }); setEditId(q.id); setOpen(true); };

  const del = async (q) => {
    try {
      await api.delete(`/quotations/${q.id}`);
      toast.success(`Quotation ${q.quotation_no} deleted`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const convertToTaxInvoice = async (q) => {
    try {
      const { data } = await api.post(`/tax-invoices/from-quotation/${q.id}`);
      toast.success(`Tax Invoice ${data.invoice_no} created`);
      nav(`/tax-invoices/${data.id}/print`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const visible = items
    .filter((q) => matchesQuery(q, search))
    .filter((q) => inDateRange(q.created_at, fFrom, fTo));

  return (
    <div className="space-y-5" data-testid="quotations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Quotations <span className="text-slate-400 text-lg font-medium">/ कोटेशन</span></h1>
          <p className="text-slate-500 text-sm">kW-based proposals with generation, ROI & EMI financing.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search quote #, customer..." testid="quotations-search-input" />
          <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setForm(empty); setEditId(null);}}}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-quotation-btn"><Plus className="w-4 h-4 mr-1"/>New Quotation</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editId ? "Edit Quotation" : "Build Solar Proposal"}</DialogTitle></DialogHeader>

              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1">Customer & System</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold text-slate-600">Customer*</Label>
                  <Select value={form.customer_id} onValueChange={v=>setForm({...form, customer_id:v})}>
                    <SelectTrigger data-testid="quotation-customer-select"><SelectValue placeholder="Select customer"/></SelectTrigger>
                    <SelectContent>{customers.map(c=><SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Field label="Quotation No. (editable)"><Input value={form.quotation_no||""} placeholder="Auto (QT-00001)" onChange={e=>setForm({...form, quotation_no:e.target.value})} data-testid="quotation-no-input"/></Field>
                <Field label="System Size (kW)*"><Input type="number" step="0.5" value={form.system_size_kw} onChange={e=>setForm({...form, system_size_kw:num(e.target.value)})} data-testid="quotation-size-input"/></Field>
                <Field label="Site Tilt (°) — blank = auto by city">
                  <Input type="number" step="1" min="0" max="60"
                    value={form.tilt_angle ?? ""}
                    onChange={e=>setForm({...form, tilt_angle: e.target.value === "" ? null : parseFloat(e.target.value)})}
                    placeholder="Auto (city latitude)"
                    data-testid="quotation-tilt-input"/>
                </Field>
                <Field label="System Cost (₹)*"><Input type="number" value={form.system_cost} onChange={e=>setForm({...form, system_cost:num(e.target.value)})} data-testid="quotation-system-cost-input"/></Field>
                <Field label="Panel Brand"><Input value={form.panel_brand} onChange={e=>setForm({...form, panel_brand:e.target.value})}/></Field>
                <Field label="Panel Wattage"><Input value={form.panel_wattage} onChange={e=>setForm({...form, panel_wattage:e.target.value})}/></Field>
                <Field label="Panel Type">
                  <Select value={form.panel_type} onValueChange={v=>setForm({...form, panel_type:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{["Bifacial","Mono PERC","TOPCon","Polycrystalline"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Structure Type">
                  <Select value={form.structure_type} onValueChange={v=>setForm({...form, structure_type:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{["HDGI","G.I. High Rise","Flush Mount","Elevated"].map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Inverter Brand"><Input value={form.inverter_brand} onChange={e=>setForm({...form, inverter_brand:e.target.value})}/></Field>
                <Field label="Inverter Capacity"><Input value={form.inverter_capacity} onChange={e=>setForm({...form, inverter_capacity:e.target.value})}/></Field>
              </div>

              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1 mt-2">Component Makes & Sizes</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="DC Wire — Size"><Input value={form.dc_wire_size} onChange={e=>setForm({...form, dc_wire_size:e.target.value})} data-testid="dc-wire-size-input"/></Field>
                <Field label="DC Wire — Brand"><Input value={form.dc_wire_brand} onChange={e=>setForm({...form, dc_wire_brand:e.target.value})} data-testid="dc-wire-brand-input"/></Field>
                <Field label="Earthing Wire — Size"><Input value={form.earthing_wire_size} onChange={e=>setForm({...form, earthing_wire_size:e.target.value})} data-testid="earthing-wire-size-input"/></Field>
                <Field label="Earthing Wire — Brand"><Input value={form.earthing_wire_brand} onChange={e=>setForm({...form, earthing_wire_brand:e.target.value})} data-testid="earthing-wire-brand-input"/></Field>
                <Field label="Armoured Cable — Size"><Input value={form.armoured_cable_size} onChange={e=>setForm({...form, armoured_cable_size:e.target.value})} data-testid="armoured-cable-size-input"/></Field>
                <Field label="Armoured Cable — Make"><Input value={form.armoured_cable_brand} onChange={e=>setForm({...form, armoured_cable_brand:e.target.value})} data-testid="armoured-cable-brand-input"/></Field>
                <Field label="Earthing Rod"><Input value={form.earthing_rod} onChange={e=>setForm({...form, earthing_rod:e.target.value})} data-testid="earthing-rod-input"/></Field>
                <Field label="Lightning Arrester"><Input value={form.lightning_arrester} onChange={e=>setForm({...form, lightning_arrester:e.target.value})} data-testid="lightning-arrester-input"/></Field>
                <Field label="ACDB — Brand"><Input value={form.acdb_brand} onChange={e=>setForm({...form, acdb_brand:e.target.value})} data-testid="acdb-brand-input"/></Field>
                <Field label="DCDB — Brand"><Input value={form.dcdb_brand} onChange={e=>setForm({...form, dcdb_brand:e.target.value})} data-testid="dcdb-brand-input"/></Field>
              </div>

              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1 mt-2">Pricing</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="GST %"><Input type="number" step="0.1" value={form.gst_percent} onChange={e=>setForm({...form, gst_percent:num(e.target.value)})}/></Field>
                <Field label="Subsidy (₹)"><Input type="number" value={form.subsidy} onChange={e=>setForm({...form, subsidy:num(e.target.value)})} data-testid="quotation-subsidy-input"/></Field>
                <Field label="Discount (₹)"><Input type="number" value={form.discount} onChange={e=>setForm({...form, discount:num(e.target.value)})}/></Field>
                <Field label="Electricity Rate (₹/unit)"><Input type="number" step="0.5" value={form.electricity_rate} onChange={e=>setForm({...form, electricity_rate:num(e.target.value)})}/></Field>
              </div>

              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1 mt-2">Loan / EMI Option (Optional)</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Loan Amount (₹)"><Input type="number" value={form.loan_amount} onChange={e=>setForm({...form, loan_amount:num(e.target.value)})} data-testid="loan-amount-input"/></Field>
                <Field label="Tenor (Months)"><Input type="number" value={form.loan_tenor_months} onChange={e=>setForm({...form, loan_tenor_months:num(e.target.value)})} data-testid="loan-tenor-input"/></Field>
                <Field label="Interest (% p.a.)"><Input type="number" step="0.1" value={form.loan_interest} onChange={e=>setForm({...form, loan_interest:num(e.target.value)})} data-testid="loan-interest-input"/></Field>
              </div>
              {loan.emi > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm flex flex-wrap gap-x-6 gap-y-1" data-testid="emi-preview">
                  <span className="font-semibold text-blue-800 flex items-center gap-1"><Landmark className="w-3.5 h-3.5"/>EMI: <span className="font-mono">{fmtINR(loan.emi)}/mo</span></span>
                  <span className="text-slate-600">Total Interest: <span className="font-mono">{fmtINR(loan.interest)}</span></span>
                  <span className="text-slate-600">Total Payable: <span className="font-mono">{fmtINR(loan.total)}</span></span>
                </div>
              )}

              <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})}/></Field>

              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 border-b pb-1 mt-2 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5"/>Warranty & Additional Items</div>
              <div className="mt-2 space-y-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Warranty Text (appears on PDF)</Label>
                  <Textarea rows={3} value={form.warranty_text||""}
                    onChange={e=>setForm({...form, warranty_text:e.target.value})}
                    placeholder="Panel: 25 years performance · Inverter: 10 years · Cables: 30 years"
                    data-testid="quotation-warranty-text"/>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-semibold text-slate-600">Additional Items / Extra Cheeze</Label>
                    <Button type="button" size="sm" variant="outline" onClick={()=>setForm({...form, additional_items:[...(form.additional_items||[]), {name:"", qty:1, rate:0, unit:"Nos"}]})} data-testid="quotation-add-extra-item">
                      <PlusCircle className="w-3.5 h-3.5 mr-1"/>Add Item
                    </Button>
                  </div>
                  {(form.additional_items || []).length > 0 && (
                    <div className="space-y-2">
                      {(form.additional_items || []).map((it, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end bg-slate-50 border border-slate-200 rounded p-2">
                          <div className="col-span-12 sm:col-span-5"><Label className="text-[10px] uppercase text-slate-500">Item Name</Label>
                            <Input value={it.name} onChange={e=>{const a=[...form.additional_items]; a[i]={...a[i], name:e.target.value}; setForm({...form, additional_items:a});}} data-testid={`extra-item-name-${i}`}/></div>
                          <div className="col-span-4 sm:col-span-2"><Label className="text-[10px] uppercase text-slate-500">Qty</Label>
                            <Input type="number" step="0.01" value={it.qty} onChange={e=>{const a=[...form.additional_items]; a[i]={...a[i], qty:num(e.target.value)}; setForm({...form, additional_items:a});}}/></div>
                          <div className="col-span-4 sm:col-span-2"><Label className="text-[10px] uppercase text-slate-500">Unit</Label>
                            <Input value={it.unit||"Nos"} onChange={e=>{const a=[...form.additional_items]; a[i]={...a[i], unit:e.target.value}; setForm({...form, additional_items:a});}}/></div>
                          <div className="col-span-3 sm:col-span-2"><Label className="text-[10px] uppercase text-slate-500">Rate (₹)</Label>
                            <Input type="number" value={it.rate} onChange={e=>{const a=[...form.additional_items]; a[i]={...a[i], rate:num(e.target.value)}; setForm({...form, additional_items:a});}} data-testid={`extra-item-rate-${i}`}/></div>
                          <div className="col-span-1 flex justify-end">
                            <Button type="button" size="icon" variant="ghost" onClick={()=>setForm({...form, additional_items: form.additional_items.filter((_,idx)=>idx!==i)})} className="text-rose-600 hover:bg-rose-50"><Trash className="w-4 h-4"/></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(form.additional_items||[]).length === 0 && (
                    <div className="text-xs text-slate-400 italic">No extra items. Add cleaning kits, extension cables, custom panels, etc.</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-1 text-sm" data-testid="generation-preview">
                  <div className="flex items-center gap-1.5 font-semibold text-emerald-800 mb-1"><Zap className="w-4 h-4"/>Generation Estimate</div>
                  <div className="flex justify-between"><span>Per Day</span><span className="font-mono">{unitsDay.toFixed(1)} units</span></div>
                  <div className="flex justify-between"><span>Per Year</span><span className="font-mono">{Math.round(yearlyUnits).toLocaleString("en-IN")} units</span></div>
                  <div className="flex justify-between"><span>Yearly Savings</span><span className="font-mono font-semibold">{fmtINR(yearlySavings)}</span></div>
                  <div className="flex justify-between"><span>Payback</span><span className="font-mono font-semibold">{payback.toFixed(1)} years</span></div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-1 text-sm" data-testid="cost-preview">
                  <div className="flex items-center gap-1.5 font-semibold text-amber-800 mb-1"><TrendingUp className="w-4 h-4"/>Final Pricing</div>
                  <div className="flex justify-between"><span>System Cost</span><span className="font-mono">{fmtINR(form.system_cost)}</span></div>
                  <div className="flex justify-between"><span>GST ({form.gst_percent}%)</span><span className="font-mono">{fmtINR(gst)}</span></div>
                  <div className="flex justify-between text-emerald-700"><span>Subsidy</span><span className="font-mono">- {fmtINR(form.subsidy)}</span></div>
                  <div className="flex justify-between text-emerald-700"><span>Discount</span><span className="font-mono">- {fmtINR(form.discount)}</span></div>
                  <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t border-amber-200 mt-1"><span>Final Cost</span><span className="font-mono">{fmtINR(finalCost)}</span></div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} disabled={!form.customer_id || !form.system_cost} data-testid="save-quotation-btn">{editId ? "Update Quotation" : "Create Quotation"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <FilterBar from={fFrom} to={fTo} onFrom={setFFrom} onTo={setFTo} onClear={()=>{setFFrom("");setFTo("");setSearch("");}} />

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Quote #</TableHead><TableHead>Customer</TableHead>
            <TableHead>System</TableHead><TableHead>System Cost</TableHead>
            <TableHead>Final Cost</TableHead><TableHead>Date</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length===0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">{search ? `No quotations matching "${search}"` : "No quotations yet."}</TableCell></TableRow>}
            {visible.map(q=>(
              <TableRow key={q.id} data-testid={`quotation-row-${q.quotation_no}`}>
                <TableCell className="font-mono text-xs">{q.quotation_no}</TableCell>
                <TableCell className="font-medium">{q.customer_name}<div className="text-xs text-slate-500">{q.customer_mobile}</div></TableCell>
                <TableCell className="font-medium text-amber-700">{q.system_size_kw} kW On-Grid</TableCell>
                <TableCell className="font-mono text-sm">{fmtINR(q.system_cost ?? q.subtotal)}</TableCell>
                <TableCell className="font-mono text-sm font-bold text-slate-900">{fmtINR(q.total)}</TableCell>
                <TableCell className="text-xs">{fmtDate(q.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Link to={`/quotations/${q.id}/print`}>
                      <Button size="sm" variant="outline" data-testid={`print-quotation-${q.quotation_no}`}><Printer className="w-3 h-3 mr-1"/>Proposal</Button>
                    </Link>
                    <WhatsAppShareButton
                      mobile={q.customer_mobile}
                      message={`Namaste ${q.customer_name || ""},\n\nYeh raha aapka Solar Proposal — ${q.system_size_kw} kW On-Grid System.\n\nProposal No.: ${q.quotation_no}\nFinal Cost: ${fmtINR(q.total)}\n\nHumari team se contact karne ke liye dhanyavaad.\n\n— MRM Renewables Energy Pvt Ltd`}
                      label="WhatsApp"
                      testid={`whatsapp-quotation-${q.quotation_no}`}
                    />
                    <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={()=>convertToTaxInvoice(q)} data-testid={`convert-tax-invoice-${q.quotation_no}`}><FileCheck className="w-3 h-3 mr-1"/>Tax Invoice</Button>
                    <Button size="sm" variant="ghost" onClick={()=>edit(q)} data-testid={`edit-quotation-${q.quotation_no}`}><Pencil className="w-3.5 h-3.5"/></Button>
                    {user?.role === "admin" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-quotation-${q.quotation_no}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Quotation {q.quotation_no}?</AlertDialogTitle>
                            <AlertDialogDescription>This will permanently delete the quotation for {q.customer_name}. This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="cancel-delete-quotation">Cancel</AlertDialogCancel>
                            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={()=>del(q)} data-testid="confirm-delete-quotation">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}

function Field({label, children}){
  return <div><Label className="text-xs font-semibold text-slate-600">{label}</Label><div className="mt-1">{children}</div></div>;
}
